import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  AggregateMetricsRequest,
  CreateMetricDefinitionRequest,
  MetricDefinitionRecord,
  MetricRecord,
  NumericAggregateMetricsResponse,
  PublishMetricsRequest,
  PublishMetricsResponse,
  QueryMetricsRequest,
  UpdateMetricDefinitionRequest,
} from "./MetricRecord";

interface Options {
  abortSignal: AbortSignal;
  orgId: string;
}

interface ListOptions extends Options {
  pageToken: string;
  pageSize: number;
}

interface QueryOptions extends Options {
  pageToken: string;
}

/**
 * Frontend mirror of the Python `roboto.domain.metrics` SDK.
 *
 * Routes wired to `packages/roboto_service/src/roboto_service/routes/metrics_router.py`:
 *   - `GET    v1/metrics/definitions/`                  → listMetricDefinitions
 *   - `POST   v1/metrics/definition/`                   → createMetricDefinition
 *   - `GET    v1/metrics/definition/{name}`             → getMetricDefinitionByName
 *   - `PUT    v1/metrics/definition/{name}`             → updateMetricDefinition
 *   - `DELETE v1/metrics/definition/{name}`             → deleteMetricDefinition
 *   - `POST   v1/metrics/`                              → publishMetrics (bulk)
 *   - `GET    v1/metrics/session/{sessionId}/`          → listMetricsBySession
 *   - `POST   v1/metrics/query`                         → queryMetrics (paginated)
 *   - `POST   v1/metrics/aggregate`                     → aggregateMetric
 */
export class MetricsService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async createMetricDefinition(
    request: CreateMetricDefinitionRequest,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/definition/`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return await response.json<MetricDefinitionRecord>();
  }

  public async getMetricDefinitionByName(
    name: string,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return await response.json<MetricDefinitionRecord>();
  }

  public async updateMetricDefinition(
    name: string,
    request: UpdateMetricDefinitionRequest,
    options?: Partial<Options>,
  ): Promise<MetricDefinitionRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.put(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return await response.json<MetricDefinitionRecord>();
  }

  public async deleteMetricDefinition(
    name: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definition/${encodeURIComponent(name)}`,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    await response.throwIfError();
  }

  public async listMetricDefinitions(
    options?: Partial<ListOptions>,
  ): Promise<PaginatedResponse<MetricDefinitionRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options?.pageSize !== undefined) {
      searchParams.append("page_size", String(options.pageSize));
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/definitions/`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return await response.json<PaginatedResponse<MetricDefinitionRecord>>();
  }

  /**
   * Fetches every page of a definitions listing, returning a flat array.
   * Convenience method for callers that need the full list.
   */
  public async getAllMetricDefinitions(
    options?: Partial<Pick<Options, "abortSignal" | "orgId">>,
  ): Promise<MetricDefinitionRecord[]> {
    const all: MetricDefinitionRecord[] = [];
    let pageToken: string | undefined;
    while (true) {
      const page = await this.listMetricDefinitions({
        abortSignal: options?.abortSignal,
        orgId: options?.orgId,
        pageToken,
      });
      all.push(...page.items);
      if (!page.next_token) {
        break;
      }
      pageToken = page.next_token;
    }
    return all;
  }

  public async publishMetrics(
    request: PublishMetricsRequest,
    options?: Partial<Options>,
  ): Promise<PublishMetricsResponse> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ orgId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return await response.json<PublishMetricsResponse>();
  }

  public async listMetricsBySession(
    sessionId: string,
    options?: Partial<Options> & { limit?: number },
  ): Promise<MetricRecord[]> {
    const searchParams = new URLSearchParams();
    if (options?.limit !== undefined) {
      searchParams.append("limit", String(options.limit));
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/session/${encodeURIComponent(sessionId)}/`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
    });

    return await response.json<MetricRecord[]>();
  }

  public async queryMetrics(
    request: QueryMetricsRequest,
    options?: Partial<QueryOptions>,
  ): Promise<PaginatedResponse<MetricRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      `v1/metrics/query`,
      searchParams,
    );

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return await response.json<PaginatedResponse<MetricRecord>>();
  }

  /**
   * Auto-paginates `queryMetrics` to exhaustion and returns the flat list. The
   * raw paginated method is exposed for callers that want to drive cursors
   * themselves (e.g., infinite-scroll components).
   */
  public async queryAllMetrics(
    request: QueryMetricsRequest,
    options?: Partial<Pick<Options, "abortSignal" | "orgId">>,
  ): Promise<MetricRecord[]> {
    const all: MetricRecord[] = [];
    let pageToken: string | undefined;
    while (true) {
      const page = await this.queryMetrics(request, {
        abortSignal: options?.abortSignal,
        orgId: options?.orgId,
        pageToken,
      });
      all.push(...page.items);
      if (!page.next_token) {
        break;
      }
      pageToken = page.next_token;
    }
    return all;
  }

  public async aggregateMetric(
    request: AggregateMetricsRequest,
    options?: Partial<Options>,
  ): Promise<NumericAggregateMetricsResponse> {
    const requestUrl = this.#httpClient.constructUrl(`v1/metrics/aggregate`);

    const response = await this.#httpClient.post(requestUrl, {
      headers: robotoHeaders({ resourceOwnerId: options?.orgId }),
      signal: options?.abortSignal,
      body: JSON.stringify(request),
    });

    return await response.json<NumericAggregateMetricsResponse>();
  }
}
